import { LanguageSwitcher } from "../language-switcher";
import I18NLink from "../i18n-link";
import { useTranslation } from "react-i18next";
import { MenuIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import { useQuery } from "@tanstack/react-query";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { getTextColor } from "@/lib/utils";

export default function Header({
  logoUrl,
  companyName,
}: {
  logoUrl?: string;
  companyName: string;
}) {
  const { t } = useTranslation("translation");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: websiteConfig } = useQuery(websiteConfigQueryOptions());

  const { getPathWithoutLocale } = useLocaleNavigation();

  //computed
  const currentPath = getPathWithoutLocale();
  const headerColor = websiteConfig?.headerColor ?? "#ffffff";

  return (
    <header
      style={{
        backgroundColor: headerColor,
        color: getTextColor(headerColor),
      }}
      className="px-4 py-2 drop-shadow z-[9999]"
    >
      <div className="mx-auto max-w-7xl flex lg:flex-col items-center lg:items-stretch">
        <nav className="relative flex flex-row justify-between items-center gap-10 order-1 lg:order-2 flex-1 min-h-14">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <div
                role="button"
                className="flex lg:hidden items-center flex-col cursor-pointer"
              >
                <MenuIcon />
                <span className="text-xs">{t("menu")}</span>
              </div>
            </DialogTrigger>
            <DialogContent className="p-0">
              <DialogHeader>
                <VisuallyHidden>
                  <DialogTitle></DialogTitle>
                  <DialogDescription></DialogDescription>
                </VisuallyHidden>
              </DialogHeader>

              <div className="flex flex-col">
                <div className="border-b p-4 flex justify-center lg:hidden">
                  <LanguageSwitcher />
                </div>
                <div className="border-b p-4 flex">
                  <I18NLink
                    className={cn(
                      "text-xl w-full text-center",
                      currentPath === "/" && "text-black font-medium"
                    )}
                    to="/"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    {t("home")}
                  </I18NLink>
                </div>

                <div className="border-b p-4 flex">
                  <I18NLink
                    className={cn(
                      "text-xl w-full text-center",
                      currentPath === "/menu" && "text-black font-medium"
                    )}
                    to="/menu"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    {t("menu")}
                  </I18NLink>
                </div>

                <div className="p-4 flex">
                  <I18NLink
                    className={cn(
                      "text-xl w-full text-center",
                      currentPath === "/#contact-info" &&
                        "text-black font-medium"
                    )}
                    to="/#contact-info"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    {t("contact")}
                  </I18NLink>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="absolute md:relative md:left-0 md:translate-x-0 left-1/2 -translate-x-1/2 flex items-center justify-center lg:justify-start lg:items-stretch lg:static lg:transform-none lg:flex-1">
            <I18NLink to="/">
              {logoUrl && (
                <img
                  alt={companyName}
                  src={logoUrl}
                  className="max-h-12 lg:max-h-14 w-auto object-contain"
                />
              )}
              {!logoUrl && (
                <h1 className="text-2xl font-bold">{companyName}</h1>
              )}
            </I18NLink>
          </div>
          <div className="flex-row gap-10 hidden lg:flex">
            <I18NLink className="text-xl" to="/">
              {t("home")}
            </I18NLink>
            <I18NLink className="text-xl" to="/menu">
              {t("order_online")}
            </I18NLink>
            <I18NLink className="text-xl" to="/#contact-info">
              {t("contact")}
            </I18NLink>
          </div>
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>
        </nav>
      </div>
    </header>
  );
}
